import { z } from 'zod';
import { Id, IsoDateTime, JsonObject, TenantId } from './common.ts';
import { Deadline } from './deadline.ts';
import { Escalation } from './escalation.ts';

/**
 * Added by 21 step 0 (brief 21 §1, §1b.4, §1b.6; ADR-021): obligations that outlive a process step or the process
 * itself (a certificate to renew, a manual reversal, a follow-up report). Core-owned (`ObligationStore` port); P2P
 * moves its pack table here (21c).
 *
 * Completion, closure, obligation are three things (§1b.6): completion = the instance reached a terminal state;
 * closure = a separate projection (`caseClosureFor`); an open obligation keeps a durable owner, a `Deadline` of any
 * kind and an `Escalation`, run by the core cron process `platform.obligation-watch`. A waiver needs a principal and
 * a reason. Open obligations stay on "Waiting on you" after closure.
 */

export const ObligationStatus = z.enum(['open', 'met', 'waived']).meta({ id: 'ObligationStatus' });
export type ObligationStatus = z.infer<typeof ObligationStatus>;

/**
 * Kind of an obligation's origin. `manual`, `pack`, `manual_reversal` (21); added by 26a (brief 26 §3.2): a gap left
 * when an agent run finished — `open_question` (an open question or missing input), `unverified_effect` (a receipt
 * `unknown`, or `succeeded` without `verifiedEffect`; met by reconciliation, never by a person), `decision_needed`
 * (the resolution owner decides the next step: retry or abandon).
 */
export const ObligationSourceKind = z
  .enum(['manual', 'pack', 'manual_reversal', 'open_question', 'unverified_effect', 'decision_needed'])
  .meta({ id: 'ObligationSourceKind' });
export type ObligationSourceKind = z.infer<typeof ObligationSourceKind>;

/** Where an obligation came from: a person or pack code, an automatic `manual_reversal` (§1b.4), or a run's gap (26). */
export const ObligationSource = z
  .object({
    kind: ObligationSourceKind,
    /** `manual_reversal`: the receipt whose tool has no `compensate`. */
    receiptId: Id.optional(),
    activationId: z.string().min(1).optional(),
    processInstanceId: Id.optional(),
    /** Added by 26a: gap id `<activationId>:<n>`, receipt id, approval request id or a pack reference. */
    ref: z.string().min(1).max(500).optional(),
    /** Added by 26a: the task (agent run) that left the gap. */
    taskId: Id.optional(),
    /**
     * Added by 26a (§2.2): the workload principal of `taskId` (the agent's workload id), recorded when the gap is
     * written so the schema can refuse `metBy` / `fulfilment.answeredBy` equal to it. Rows without it are checked by
     * the service against the task row.
     */
    workloadPrincipalId: z.string().min(1).max(200).optional(),
    /** Added by 26a: a free note on the source (e.g. "role X has no members"). */
    note: z.string().min(1).max(500).optional(),
  })
  .meta({ id: 'ObligationSource' });
export type ObligationSource = z.infer<typeof ObligationSource>;

/** Added by 26a (§3.2): who answers an obligation. Absent on the obligation = the owner answers. */
export const ObligationRespondent = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('principal'), principalId: z.string().min(1) }),
    z.object({ kind: z.literal('role'), role: z.string().min(1) }),
    z.object({
      kind: z.literal('external'),
      externalId: z.string().min(1).optional(),
      contact: z
        .object({ name: z.string().min(1).max(200).optional(), email: z.email().optional() })
        .optional(),
    }),
    z.object({ kind: z.literal('system'), system: z.string().min(1).max(200) }),
  ])
  .meta({ id: 'ObligationRespondent' });
export type ObligationRespondent = z.infer<typeof ObligationRespondent>;

/** Added by 26a (§3.2): how the respondent is asked. */
export const ObligationChannelKind = z
  .enum(['task', 'email', 'inapp', 'upstream', 'reconciliation'])
  .meta({ id: 'ObligationChannelKind' });
export type ObligationChannelKind = z.infer<typeof ObligationChannelKind>;

/**
 * Added by 26a phase 4 (§4.5): where the latest ask stands. `sent` (the notification service accepted it),
 * `unknown` (the governed send's outcome is unknown; the ledger reconciles it), `awaiting_approval` (policy needs an
 * approval before the external ask is sent), `refused` (phase 5: the approver refused that send), `skipped` (nobody to send to: the item shows on "Waiting on you" only).
 * A failed ask is the `undeliverable` flag, not a status.
 */
export const ObligationAskStatus = z
  .enum(['sent', 'unknown', 'awaiting_approval', 'refused', 'skipped'])
  .meta({ id: 'ObligationAskStatus' });
export type ObligationAskStatus = z.infer<typeof ObligationAskStatus>;

/**
 * Added by 26a phase 5 (§6.9, §7): the decision on the approval of a held external ask. `approved` → the ask is sent
 * with the same idempotency key; `refused` (askStatus `refused`) → the owner gets the answer another way ("Answer for
 * {contact}") or waives.
 */
export const ObligationAskApproval = z
  .object({
    decision: z.enum(['approved', 'refused']),
    /** Principal who decided. */
    by: z.string().min(1),
    reason: z.string().max(2000).optional(),
    at: IsoDateTime,
  })
  .meta({ id: 'ObligationAskApproval' });
export type ObligationAskApproval = z.infer<typeof ObligationAskApproval>;

/** Added by 26a (§3.2): the channel of the ask and when it was sent. */
export const ObligationChannel = z
  .object({
    kind: ObligationChannelKind,
    askedAt: IsoDateTime.optional(),
    notificationId: z.string().min(1).optional(),
    /** Added by 26a phase 4: ledger action id of the latest governed ask (reconciliation, approval reference). */
    actionId: Id.optional(),
    /** Added by 26a phase 4: where the latest ask stands. */
    askStatus: ObligationAskStatus.optional(),
    /** Added by 26a phase 4: asks sent so far (1 = the first; "Ask again" n uses key `obligation-ask:<id>:<n>`). */
    asks: z.number().int().nonnegative().optional(),
    /** Added by 26a phase 5: the approval request opened for a held ask (§7 "approval request id on the obligation"). */
    approvalRequestId: z.string().min(1).optional(),
    /** Added by 26a phase 5: its decision, once made. */
    approval: ObligationAskApproval.optional(),
  })
  .meta({ id: 'ObligationChannel' });
export type ObligationChannel = z.infer<typeof ObligationChannel>;

/** Largest default answer (`{ answer: string }`, 1–4000 chars) when the obligation has no `answerSchemaRef`. */
export const OBLIGATION_ANSWER_TEXT_MAX = 4000;

/** Added by 26a (§3.2): the default answer schema (absent `answerSchemaRef`): one required text field `answer`. */
export const DefaultObligationAnswer = z
  .object({ answer: z.string().min(1).max(OBLIGATION_ANSWER_TEXT_MAX) })
  .meta({ id: 'DefaultObligationAnswer' });
export type DefaultObligationAnswer = z.infer<typeof DefaultObligationAnswer>;

/**
 * Added by 26a (§3.2, invariant 3): an answer recorded on an obligation. `eventId` is the inbox event id (fulfil is
 * idempotent on it); `revises` names the `eventId` of the answer it replaces (kept in `history`).
 */
/** Added by the 26a review round: what produced an obligation's fulfilment (see `ObligationFulfilment.kind`). */
export const ObligationFulfilmentKind = z
  .enum(['answer', 'decision', 'evidence', 'reconciliation'])
  .meta({ id: 'ObligationFulfilmentKind' });
export type ObligationFulfilmentKind = z.infer<typeof ObligationFulfilmentKind>;

export const ObligationFulfilment = z
  .object({
    eventId: z.string().min(1).max(255),
    answeredBy: z.string().min(1),
    /** The owner recorded the answer for this respondent (external id or a note). */
    onBehalfOf: z.string().min(1).max(500).optional(),
    at: IsoDateTime,
    answer: JsonObject,
    revises: z.string().min(1).max(255).optional(),
    /**
     * Added by the 26a review round: how it was fulfilled. `decision` only from `POST …/decide` (the only fulfilment a
     * run reads a retry / abandon decision from); `evidence` from `PATCH …` met with an evidence link;
     * `reconciliation` from the action ledger. Absent = `answer` (rows written before the field).
     */
    kind: ObligationFulfilmentKind.optional(),
  })
  .meta({ id: 'ObligationFulfilment' });
export type ObligationFulfilment = z.infer<typeof ObligationFulfilment>;

/** Added by 26a (§3.2): the ask could not be delivered (after the ledger's retries, or no contact address). */
export const ObligationUndeliverable = z
  .object({ at: IsoDateTime, reason: z.string().min(1).max(500), attempts: z.number().int().nonnegative() })
  .meta({ id: 'ObligationUndeliverable' });
export type ObligationUndeliverable = z.infer<typeof ObligationUndeliverable>;

/** Added by 26a (§3.2): what the owner may decide on a `decision_needed` obligation. */
export const ObligationDecisionOption = z.enum(['retry', 'abandon']).meta({ id: 'ObligationDecisionOption' });
export type ObligationDecisionOption = z.infer<typeof ObligationDecisionOption>;

export const Obligation = z
  .object({
    obligationId: Id,
    tenantId: TenantId,
    caseId: Id,
    title: z.string().min(1).max(300),
    description: z.string().max(4000).optional(),
    ownerRole: z.string().min(1),
    assigneePrincipalId: z.string().min(1).optional(),
    deadline: Deadline,
    escalation: Escalation,
    status: ObligationStatus,
    /** Evidence that it was met (document id, case link, receipt id). */
    evidenceLink: z.string().min(1).max(500).optional(),
    metBy: z.string().min(1).optional(),
    metAt: IsoDateTime.optional(),
    waivedBy: z.string().min(1).optional(),
    waiveReason: z.string().min(1).max(2000).optional(),
    waivedAt: IsoDateTime.optional(),
    source: ObligationSource.default({ kind: 'manual' }),
    /** Added by 21c: roles added by an `add_role` escalation (their holders may also act on it). */
    additionalRoles: z.array(z.string().min(1)).optional(),
    /** Added by 21c: when `platform.obligation-watch` ran the escalation (once per obligation and deadline). */
    escalatedAt: IsoDateTime.optional(),
    createdBy: z.string().min(1),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    /** Added by 26a (§3.2): who answers (absent = the owner). */
    respondent: ObligationRespondent.optional(),
    /** Added by 26a: how the respondent is asked. */
    channel: ObligationChannel.optional(),
    /** Added by 26a: a schema registered through `registerWorkItemOutputSchema`; absent = `DefaultObligationAnswer`. */
    answerSchemaRef: z.string().min(1).max(300).optional(),
    /** Added by 26a: the answer that counts (invariant 3: fulfilment is an event). */
    fulfilment: ObligationFulfilment.optional(),
    /** Added by 26a: superseded answers, kept as evidence (oldest first). */
    history: z.array(ObligationFulfilment).optional(),
    /** Added by 26a: the ask could not be delivered. A flag, not a state. */
    undeliverable: ObligationUndeliverable.optional(),
    /** Added by 26a: activation id of the continuation run this obligation's settlement started. */
    continuationRunId: z.string().min(1).max(200).optional(),
    /** Added by 26a: when the deadline was breached (`obligation.overdue`). A flag, not a state. */
    overdueAt: IsoDateTime.optional(),
    /** Added by 26a: the decisions offered on a `decision_needed` obligation. */
    options: z.array(ObligationDecisionOption).min(1).max(2).optional(),
  })
  .superRefine((o, ctx) => {
    if (o.status === 'waived' && (o.waivedBy === undefined || o.waiveReason === undefined)) {
      ctx.addIssue({
        code: 'custom',
        message: 'a waiver needs waivedBy and waiveReason',
        path: ['waiveReason'],
      });
    }
    // 26a (§3.2): met needs proof — an answer (fulfilment) or evidence (existing rows keep evidenceLink).
    if (o.status === 'met' && o.fulfilment === undefined && o.evidenceLink === undefined) {
      ctx.addIssue({ code: 'custom', message: 'met needs fulfilment or evidenceLink', path: ['fulfilment'] });
    }
    // 26a (§2.2): an agent can open an obligation but never meet it. The workload principal is known to the schema
    // only when the writer recorded it on the source; the service checks the task row otherwise.
    const workload = o.source.workloadPrincipalId;
    if (workload !== undefined && (o.metBy === workload || o.fulfilment?.answeredBy === workload)) {
      ctx.addIssue({
        code: 'custom',
        message: 'the workload principal of the source task cannot meet its own obligation',
        path: ['metBy'],
      });
    }
    if (o.options !== undefined && o.source.kind !== 'decision_needed') {
      ctx.addIssue({
        code: 'custom',
        message: 'options are for decision_needed obligations',
        path: ['options'],
      });
    }
  })
  .meta({ id: 'Obligation' });
export type Obligation = z.infer<typeof Obligation>;

/** Body of `POST /cases/{caseId}/obligations`. */
export const CreateObligationBody = z
  .object({
    title: z.string().min(1).max(300),
    description: z.string().max(4000).optional(),
    ownerRole: z.string().min(1),
    assigneePrincipalId: z.string().min(1).optional(),
    deadline: Deadline,
    escalation: Escalation,
  })
  .meta({ id: 'CreateObligationBody' });
export type CreateObligationBody = z.infer<typeof CreateObligationBody>;

/**
 * Body of `PATCH /cases/{caseId}/obligations/{obligationId}`: mark met (with evidence), waive (reason required),
 * reassign, or move an extendable deadline (`statutory` refused).
 */
export const UpdateObligationBody = z
  .object({
    status: z.enum(['met', 'waived']).optional(),
    evidenceLink: z.string().min(1).max(500).optional(),
    reason: z.string().min(1).max(2000).optional(),
    assigneePrincipalId: z.string().min(1).optional(),
    deadline: Deadline.optional(),
  })
  .superRefine((b, ctx) => {
    if (b.status === 'waived' && b.reason === undefined) {
      ctx.addIssue({ code: 'custom', message: 'a waiver needs a reason', path: ['reason'] });
    }
  })
  .meta({ id: 'UpdateObligationBody' });
export type UpdateObligationBody = z.infer<typeof UpdateObligationBody>;

/**
 * `ProcessDefinitionMeta.closePolicy` (§1b.6). Default `require_obligations_met`: a succeeded instance with open
 * obligations leaves its case `in_progress`, "awaiting closure". `allow_open_obligations` closes it anyway.
 */
export const ClosePolicy = z
  .enum(['require_obligations_met', 'allow_open_obligations'])
  .meta({ id: 'ClosePolicy' });
export type ClosePolicy = z.infer<typeof ClosePolicy>;

/**
 * The closure rule of §1b.6 for a case whose process instances all **succeeded**: no open obligations → `closed`;
 * open obligations → `closed` only with `allow_open_obligations`, otherwise `in_progress` with `awaitingClosure`.
 */
export function caseClosureFor(input: { openObligations: number; closePolicy?: ClosePolicy }): {
  state: 'closed' | 'in_progress';
  awaitingClosure: boolean;
} {
  if (input.openObligations === 0) return { state: 'closed', awaitingClosure: false };
  if (input.closePolicy === 'allow_open_obligations') return { state: 'closed', awaitingClosure: false };
  return { state: 'in_progress', awaitingClosure: true };
}

export const OBLIGATION_EVENT_TYPES = {
  opened: 'obligation.opened',
  met: 'obligation.met',
  waived: 'obligation.waived',
  /** Added by 26a: the deadline passed (`overdueAt` set). */
  overdue: 'obligation.overdue',
  /** Added by 26a: the escalation ran. */
  escalated: 'obligation.escalated',
  /** Added by 26a: the ask could not be delivered. */
  undeliverable: 'obligation.undeliverable',
  /** Added by 26a: an answer recorded on a met obligation as a revision. */
  answered: 'obligation.answered',
} as const;

/** `obligation.opened|met|waived` (21); `obligation.overdue|escalated|undeliverable|answered` (26a). */
export const ObligationEventData = z
  .object({ tenantId: TenantId, caseId: Id, obligation: Obligation, commandId: z.string().min(1) })
  .meta({ id: 'ObligationEventData' });
export type ObligationEventData = z.infer<typeof ObligationEventData>;
